<template>
  <!-- Admin User Impersonation Section -->
  <div v-if="isMinimized" class="justify-center flex p-2">
    <BaseButton
      iconSmall
      :icon="'shield-account'"
      @click="openUserSearchDialog"
    />
  </div>
  <div
    v-if="
      userProfile.IsAdmin &&
      route.path.includes('user-settings') &&
      !isMinimized
    "
    class="mb-2 px-4"
  >
    <div class="bg-white rounded-lg shadow p-3 border border-gray-100">
      <div class="flex items-center mb-2">
        <i class="mdi mdi-shield-account text-primary mr-2"></i>
        <h4 class="text-primary text-sm font-medium m-0">
          {{ $t('Admin Search') }}
        </h4>
        <span
          class="ml-2 px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full"
          >Admin</span
        >
      </div>

      <div class="flex flex-col gap-2 w-full">
        <button
          @click="openUserSearchDialog"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left text-gray-500 hover:border-primary focus:outline-none focus:ring-1 focus:ring-primary flex items-center"
        >
          <i class="mdi mdi-magnify text-gray-400 mr-2"></i>
          {{ $t('Search...') }}
        </button>
      </div>

      <!-- Selected User -->
      <div
        v-if="selectedUser"
        class="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between"
      >
        <div class="flex items-center">
          <i class="mdi mdi-account-check text-green-600 mr-2 text-sm"></i>
          <div>
            <div class="text-xs text-green-800">{{ $t('Viewing') }}:</div>
            <div class="font-medium text-green-700 text-sm">
              {{ selectedUser.firstname }}
              {{ selectedUser.lastname }}
            </div>
          </div>
        </div>
        <button
          class="px-2 py-1 text-xs bg-transparent text-green-700 hover:bg-green-100 rounded-md focus:outline-none"
          @click="setSelectedUser(null)"
        >
          {{ $t('Clear') }}
        </button>
      </div>
    </div>
  </div>
  <!-- End Admin User Impersonation Section -->
  <nav class="app-sidebar__nav">
    <ul role="list" class="flex flex-1 flex-col gap-y-7">
      <li>
        <ul role="list" class="-mx-2 space-y-3">
          <li
            v-for="(item, index) in menuItems"
            :key="index"
            :class="{
              active: isActive(item),
            }"
          >
            <a
              v-if="item.name === 'Account'"
              @click="handleClick(index, item, $event)"
              :href="isAlpha ? item.devPath : item.appPath"
              target="_blank"
              :class="[
                { 'justify-center': isMinimized },
                { 'bg-gray-50 border-l-4 border-primary': isActive(item) },
                {
                  'hover:bg-gray-50 border-l-4 border-transparent':
                    !isActive(item),
                },
              ]"
              class="group flex items-center gap-x-2 rounded-md py-1.5 px-2 text-sm/6 text-primary"
            >
              <i :class="item.icon" class="text-xl"></i>
              <span class="app-sidebar__nav--text">{{ $t(item.name) }}</span>
            </a>
            <NuxtLink
              v-else-if="!item.children"
              @click="handleClick(index, item, $event)"
              :to="{ path: route.fullPath, query: item.query }"
              :class="[
                { 'justify-center': isMinimized },
                { 'bg-gray-50 border-l-4 border-primary': isActive(item) },
              ]"
              class="group flex items-center gap-x-2 rounded-md py-1.5 px-2 text-sm/6 text-primary"
            >
              <i :class="item.icon" class="text-xl"></i>
              <span class="app-sidebar__nav--text">{{ $t(item.name) }}</span>
            </NuxtLink>
            <Disclosure as="div" v-else>
              <DisclosureButton
                @click="togglePanel(index)"
                :class="[
                  { 'justify-center': isMinimized },
                  { 'bg-gray-50': isActive(item) },
                  { 'hover:bg-gray-50': !isActive(item) },
                ]"
                class="flex w-full items-center gap-x-2 border-l-4 border-transparent rounded-md py-1.5 px-2 text-left text-sm/6 text-primary"
              >
                <i :class="item.icon" class="text-xl"></i>
                <span class="app-sidebar__nav--text">{{ item.name }}</span>
                <ChevronRightIcon
                  :class="[
                    panelToggle === index
                      ? 'rotate-90 text-primary'
                      : 'text-gray-200',
                    'ml-auto size-5 shrink-0 app-sidebar__nav--text',
                  ]"
                  aria-hidden="true"
                />
              </DisclosureButton>
              <ul
                v-if="panelToggle === index"
                class="mt-1 ml-5 mr-2 border-l-2 border-gray-100 app-sidebar__nav--text"
              >
                <li v-for="subItem in item.children" :key="subItem.name">
                  <!-- 44px -->
                  <DisclosureButton
                    :as="NuxtLink"
                    @click="handleClick(index, subItem, $event)"
                    :to="{ path: route.fullPath, query: subItem.query }"
                    :class="[
                      {
                        'bg-gray-50 border-l-4 border-primary':
                          isActive(subItem),
                      },
                    ]"
                    class="block rounded-md my-1 ml-3 py-1.5 px-2 text-sm/6 text-primary"
                  >
                    <span class="">{{ subItem.name }}</span></DisclosureButton
                  >
                </li>
              </ul>
            </Disclosure>
          </li>
        </ul>
      </li>
    </ul>
  </nav>
  <AdminUserSearchDialog
    :open="userSearchDialogOpen"
    @close="userSearchDialogOpen = false"
    @select-user="setSelectedUser"
  />
</template>

<script lang="ts" setup>
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue'
import { ChevronRightIcon, ArrowLeftIcon } from '@heroicons/vue/20/solid'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { Bars3Icon } from '@heroicons/vue/20/solid'
import AdminUserSearchDialog from '~/components/User/AdminUserSearchDialog.vue'

const route = useRoute()
const permissions = usePermissions()
const subscriptionApi = useSubscriptions()
const { userProfile } = useKeycloak()
const { $features, $bus } = useNuxtApp()
const NuxtLink = resolveComponent('nuxt-link')
const config = useRuntimeConfig()

const selectedUser = ref<any>(null)
const org = ref<boolean>(false)
const orgId = ref<number>()
const panelToggle = ref<number | null>(null)
const userSearchDialogOpen = ref(false)
const loadingIndex = ref<number | null>(null)
const collId = ref(route.query.collId || null)

const props = withDefaults(
  defineProps<{
    isMinimized?: boolean
    showInvitations?: boolean
  }>(),
  {
    showInvitations: false,
  }
)

onMounted(async () => {
  if (!route.path.includes('user-settings')) {
    const matchedIndex = findMatchingIndex(menuItems, route.query.view)
    if (matchedIndex !== null) {
      panelToggle.value = matchedIndex
    }
  } else {
    const { organization } = await subscriptionApi.getSubscription({
      userId: userProfile?.UserId,
      userName: userProfile?.UserName,
    })
    if (organization) {
      permissions.setTeamPermissions(organization)
      orgId.value = organization.id
      org.value = organization
    }
  }
})

watch(
  () => route.fullPath,
  () => {
    setTimeout(() => {
      loadingIndex.value = null
    }, 100)
  }
)

const isAlpha = computed(() => {
  return config.public.ENVIRONMENT_NAME === 'alpha'
})

const openUserSearchDialog = () => {
  userSearchDialogOpen.value = true
}

function setSelectedUser(user: any) {
  selectedUser.value = user

  $bus.emit('setUser', user)
}

const findMatchingIndex = (menu, targetView) => {
  for (const item of menu) {
    if (
      item.query?.view === targetView ||
      item.children?.some(child => child.query?.view === targetView)
    ) {
      return item.index
    }
  }
  return null
}

function isActive(item: any): boolean {
  return item.query?.view === route.query.view
}

const togglePanel = (index: number) => {
  panelToggle.value = panelToggle.value === index ? null : index
}

function handleClick(index: number, item: any, event: MouseEvent): void {
  const isAlreadyActive = isActive(item)
  const isNewTab = event.ctrlKey || event.metaKey

  if (!isAlreadyActive && !isNewTab) {
    loadingIndex.value = index
  }
}

const menuItems = computed(() => {
  const { path, query } = route
  if (path.includes('collection-settings')) {
    let membersChildren = [
      {
        name: 'Collection members',
        path: '/collection-settings',
        query: { collId: collId.value, view: 'members' },
      },
    ]
    if (props.showInvitations) {
      membersChildren.push({
        name: 'Invitations',
        path: '/collection-settings',
        query: { collId: collId.value, view: 'invites' },
      })
    }

    return [
      {
        index: 0,
        name: 'General Settings',
        icon: 'mdi mdi-cog',
        path: '/collection-settings',
        query: { collId: collId.value, view: 'general' },
      },
      {
        index: 1,
        name: 'Members',
        icon: 'mdi mdi-account-group',
        children: membersChildren,
      },
      {
        index: 2,
        name: 'Tags',
        icon: 'mdi mdi-tag',
        children: [
          {
            name: 'Textual tags',
            path: '/collection-settings',
            query: { collId: collId.value, view: 'textual' },
          },
          {
            name: 'Structural tags',
            path: '/collection-settings',
            query: { collId: collId.value, view: 'structural' },
          },
          {
            name: 'Relational tags',
            path: '/collection-settings',
            query: { collId: collId.value, view: 'relational' },
          },
        ],
      },
      {
        index: 3,
        name: 'Imports',
        icon: 'mdi mdi-import',
        path: '/collection-settings',
        query: { collId: collId.value, view: 'imports' },
      },
      {
        index: 4,
        name: 'Downloads',
        icon: 'mdi mdi-download',
        path: '/collection-settings',
        query: { collId: collId.value, view: 'downloads' },
      },
      ...(permissions.myRole?.isOwner
        ? [
            {
              index: 5,
              name: 'Usage and Limits',
              icon: 'mdi mdi-poll',
              path: '/collection-settings',
              query: { collId: collId.value, view: 'usage' },
            },
          ]
        : []),
    ]
  } else if (path.includes('user-settings')) {
    permissions.setTeamPermissions(org.value)
    let baseMenu = [
      {
        index: 0,
        name: 'Account',
        icon: 'mdi mdi-cog',
        appPath: 'https://account.readcoop.eu/auth/realms/readcoop/account',
        devPath:
          'https://account.readcoop.eu/authTesting/realms/readcoop/account',
        // query: { view: 'account' },
      },
      {
        index: 1,
        name: 'Usage & Limits',
        icon: 'mdi mdi-poll',
        children: [
          {
            name: 'Personal usage',
            path: '/user-settings',
            query: { view: 'personal', userId: selectedUser?.value?.userId },
          },
          {
            name: 'Collection usage',
            path: '/user-settings',
            query: { view: 'collection', userId: selectedUser?.value?.userId },
          },
          {
            name: 'Subscription',
            path: '/user-settings',
            query: {
              view: 'subscription',
              userId: selectedUser?.value?.userId,
            },
          },
        ],
      },
      ...($features.notifications
        ? [
            {
              name: 'Notifications',
              icon: 'mdi mdi-bell',
              path: '/user-settings',
              query: { view: 'notifications' },
            },
          ]
        : []),
      ...($features.virtualKeyboardSettings
        ? [
            {
              name: 'Virtual Keyboards',
              icon: 'mdi mdi-keyboard',
              path: '/user-settings',
              query: { view: 'keyboards' },
            },
          ]
        : []),
      // {
      //     name: 'Virtual Keyboards',
      //     icon: 'mdi mdi-keyboard',
      //     path: '/user-settings',
      //     query: { view: 'keyboards' },
      // },
    ]
    if (permissions.teams[orgId.value]?.view) {
      let orgMenu = {
        index: 2,
        name: 'Manage organisation',
        icon: 'mdi mdi-domain',
        children: [
          {
            name: 'Organisation account',
            path: '/user-settings',
            query: {
              view: 'org-account',
              orgId: org.value.id,
              userId: selectedUser?.value?.userId,
            },
          },
          {
            name: 'User seats',
            path: '/user-settings',
            query: {
              view: 'user-seats',
              orgId: org.value.id,
              userId: selectedUser?.value?.userId,
            },
          },
        ],
      }
      if (permissions.teams[org.value.id]?.inviteMembers) {
        orgMenu.children.push({
          name: 'User invitations',
          path: '/user-invites',
          query: {
            view: 'user-invites',
            orgId: org.value.id,
            userId: selectedUser?.value?.userId,
          },
        })
      }

      if (org.value.planId != 12 && org.value.planId != 13) {
        orgMenu.children.push({
          name: 'Metagrapho API',
          path: 'https://www.transkribus.org/metagrapho/documentation',
          query: undefined,
        })
      }

      baseMenu.push(orgMenu)
    }

    panelToggle.value = findMatchingIndex(baseMenu, route.query.view)
    return baseMenu
  }
})
</script>

<style></style>
